import React, { useState } from "react";

import {
  mdiPlus,
  mdiPlusNetworkOutline,
  mdiTrashCanOutline,
  mdiUpload,
  mdiUploadNetworkOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import AutoCuePointsModal from "./AutoCuePointsModal";
import Button from "../../button/Button";
import FileUpload from "../../utils/FileUpload";

import type { JSX } from "react";

import type { MediaCuepointAdded } from "../../../api/MediaAPI.types";
import type { CuepointsFormValues } from "../TabContentCuePoints";

interface CuePointsTableActionsProps {
  addCuepoint: () => void;
  deleteAllCuepoints: () => void;
  importCuepoints: (cuepointsDataFile: File) => void;
  mediaDurationMs: number | null;
  addCuepointList: (cuepointList: MediaCuepointAdded[]) => void;
}

function CuePointsTableActions({
  addCuepoint,
  deleteAllCuepoints,
  importCuepoints,
  mediaDurationMs,
  addCuepointList,
}: CuePointsTableActionsProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const { getValues } = useFormContext<CuepointsFormValues>();
  const [isOpenAutoCuePointsModal, setIsOpenAutoCuePointsModal] =
    useState(false);

  const hasEmptyTimecode = getValues().cuepoints.some(
    (cuepoint) => cuepoint.timecode === "",
  );

  const dropdownItemList = [
    {
      label: t("cuePoint"),
      onClick: addCuepoint,
      icon: (
        <Icon
          className="text-primary"
          path={mdiPlusNetworkOutline}
          rotate={90}
          size={0.875}
        />
      ),
    },
    {
      label: t("autoCut"),
      onClick: (): void => setIsOpenAutoCuePointsModal(true),
      icon: (
        <Icon
          className="text-primary"
          path={mdiUploadNetworkOutline}
          rotate={90}
          size={0.875}
        />
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className="flex justify-between gap-2">
        <Button
          data-tooltip-content={t("existingEmptyTimecode")}
          data-tooltip-hidden={!hasEmptyTimecode}
          data-tooltip-id="add-timecode-button"
          dropdownItemList={dropdownItemList}
          isDisabled={hasEmptyTimecode}
          size="md"
          type="button"
        >
          <Icon path={mdiPlus} size={0.75} />
          {t("addCuePoint")}
        </Button>
        <Button size="md" type="button">
          <FileUpload
            accept=".json"
            id="upload-file-input-cuepoints"
            onAddFile={importCuepoints}
            label={
              <div className="flex items-center gap-2 text-nowrap">
                <Icon path={mdiUpload} size={0.75} />
                {t("uploadJsonFile")}
              </div>
            }
          />
        </Button>
        <Button
          onClick={deleteAllCuepoints}
          size="md"
          type="button"
          variant="delete"
        >
          <div className="flex items-center gap-2 text-nowrap">
            <Icon path={mdiTrashCanOutline} size={0.75} />
            {t("deleteAll")}
          </div>
        </Button>
      </div>
      <AutoCuePointsModal
        addCuepointList={addCuepointList}
        isOpen={isOpenAutoCuePointsModal}
        mediaDurationMs={mediaDurationMs}
        onClose={(): void => setIsOpenAutoCuePointsModal(false)}
      />
    </React.Fragment>
  );
}

export default CuePointsTableActions;
